import { type ReactNode, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom-v5-compat';

import { Loader } from 'src/components/Loader';
import { isReviewerRole, type User } from 'src/data/user';
import { queryProjectCustomerId } from 'src/graphql/project';
import { Authentication } from 'src/lib/Authentication';
import { captureMessage } from 'src/lib/sentry';
import { ROUTES } from 'src/routes';
// @ts-expect-error - no TypeScript definitions yet
import { useCustomerActions } from 'src/store/customer';
// @ts-expect-error - no TypeScript definitions yet
import { useCurrentUser } from 'src/store/user';

interface ProjectLinkAccountGateProps {
  children: ReactNode;
}

const hasReviewerAccount = (user: User) =>
  Boolean(user.customers?.some((customer) => isReviewerRole(customer.role)));

/**
 * Wraps the project page of both route trees. A project can belong to another account than the
 * selected one: a link from an email, a sign-in that returns to a project, an accepted reviewer
 * invite. The user can be a reviewer in that account, or a member, and each mode has its own
 * project page. For a user with a reviewer account, the gate picks the project's account and its
 * mode before the project page renders.
 */
export const ProjectLinkAccountGate = ({ children }: ProjectLinkAccountGateProps) => {
  const { pathname } = useLocation();
  const user: User | null = useCurrentUser();
  const customerActions = useCustomerActions();
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(null);

  const projectId = matchPath(`${ROUTES.PROJECTS}/:projectId`, pathname)?.params.projectId ?? null;
  const onProjectLink = Boolean(projectId) && Authentication.isAuthenticated();
  const waitingForUser = onProjectLink && !user;
  const resolving =
    onProjectLink && user !== null && hasReviewerAccount(user) && projectId !== resolvedProjectId;

  useEffect(() => {
    if (!resolving || !projectId) {
      return;
    }

    queryProjectCustomerId({ id: Number(projectId) })
      .then((customerId) => customerId && customerActions.selectCurrentCustomer(customerId))
      .catch((error) => {
        // The project page shows the error the project's own query gets.
        captureMessage("Couldn't find the account of a project link", {
          level: 'warning',
          extra: { error, projectId },
        });
      })
      .finally(() => setResolvedProjectId(projectId));
  }, [resolving, projectId, customerActions]);

  if (waitingForUser || resolving) {
    return <Loader />;
  }

  return children;
};
